import { useInView } from 'react-intersection-observer'
import { Link, useNavigate } from 'react-router-dom'
import PullToRefresh from 'react-simple-pull-to-refresh'

import { LoadingSpinner } from 'components/shared/LoadingSpinner'
import { Button } from 'components/ui/Button'
import { StatusBadge } from 'components/ui/StatusBadge'
import { SurfaceCard } from 'components/ui/SurfaceCard'
import { useAuth } from 'contexts/AuthContext'
import { useUserConversationsInfiniteQuery } from 'modules/coaching/queries'
import { isMuxPlaybackReady } from 'utils/videoUrlResolver'

import type { components } from '@startech/openapi'

type ConversationWithFile = components['schemas']['ConversationWithFile']

const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

const getStatusBadge = (status?: string) => {
  switch (status) {
    case 'ready': {
      return <StatusBadge status="ready" />
    }
    case 'processing': {
      return <StatusBadge status="processing" />
    }
    case 'pending': {
      return <StatusBadge status="pending" />
    }
    case 'failed': {
      return <StatusBadge status="failed" />
    }
    default: {
      return undefined
    }
  }
}

export const MyVideosScreen = () => {
  /* --- Section 1: Hooks --- */
  const navigate = useNavigate()
  const { user } = useAuth()
  const { data, error, fetchNextPage, hasNextPage, isError, isFetchingNextPage, isLoading, refetch } =
    useUserConversationsInfiniteQuery(user?.id)
  const { ref } = useInView({
    onChange: inView => {
      if (inView && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage()
      }
    },
  })

  /* --- Section 3: Derived Values --- */
  const conversations = data?.pages.flatMap((page: { data: ConversationWithFile[] }) => page.data) ?? []

  /* --- Section 5: Early Returns --- */
  if (isLoading) {
    return (
      <div className="athlete-app">
        <div className="athlete-loading-container">
          <LoadingSpinner />
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="athlete-app">
        <div className="athlete-error-container">
          <div className="athlete-error-content">
            <p className="athlete-error-message">{error instanceof Error ? error.message : 'Something went wrong.'}</p>
          </div>
        </div>
      </div>
    )
  }

  /* --- Section 6: Render --- */
  return (
    <div className="athlete-app">
      <PullToRefresh onRefresh={() => refetch().then(() => undefined)} className="pull-to-refresh-wrapper">
        <div className="my-videos-container">
          {conversations.length === 0 ? (
            <div className="my-videos-empty">
              <p className="my-videos-empty-text">You haven&apos;t uploaded any videos yet.</p>
              <Button onClick={() => navigate('/new')} variant="secondary">
                Upload Your First Video
              </Button>
            </div>
          ) : (
            <>
              <div className="my-videos-grid">
                {conversations.map(conversation => {
                  const file = conversation.file
                  const title = file?.title ?? conversation.title ?? 'Untitled Session'
                  const muxThumbnail =
                    file && isMuxPlaybackReady(file) ? (file.playback?.thumbnailUrl ?? undefined) : undefined
                  const thumbnailUrl = muxThumbnail

                  return (
                    <SurfaceCard asChild key={conversation.id} variant="interactive">
                      <Link className="video-card" to={`/c/${conversation.id}`}>
                        <span className="video-card-thumbnail">
                          {thumbnailUrl ? (
                            <img
                              src={thumbnailUrl}
                              className="video-thumbnail video-thumbnail-image"
                              alt=""
                              loading="lazy"
                            />
                          ) : (
                            <span className="video-thumbnail-placeholder">
                              <svg
                                width="48"
                                height="48"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M8 5v14l11-7z"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </span>
                          )}
                        </span>
                        <span className="video-card-content">
                          <span className="video-card-title">{title}</span>
                          <span className="video-card-meta">
                            <span className="video-card-date">
                              Updated{' '}
                              {formatDate(
                                conversation.updated_at ?? conversation.created_at ?? new Date().toISOString()
                              )}
                            </span>
                            {getStatusBadge(file?.processing_status ?? undefined)}
                          </span>
                        </span>
                      </Link>
                    </SurfaceCard>
                  )
                })}
              </div>
              <div ref={ref} className="my-videos-loader">
                {isFetchingNextPage ? <LoadingSpinner /> : undefined}
              </div>
            </>
          )}
        </div>
      </PullToRefresh>
    </div>
  )
}
